/**
 * Date range control shared by every analytics screen: a few presets and a
 * custom range. Controlled — the screen owns the range and decides whether to
 * filter in memory or re-query.
 */

import { rangeForPreset, type DateRange, type RangePreset } from '../../lib/analytics/metrics';

export interface RangeValue {
  preset: RangePreset;
  range: DateRange;
}

const PRESETS: { value: RangePreset; label: string }[] = [
  { value: '7d', label: 'Last 7 days' },
  { value: '30d', label: 'Last 30 days' },
  { value: 'year', label: 'This academic year' },
  { value: 'all', label: 'All time' },
  { value: 'custom', label: 'Custom' },
];

const toInput = (ms: number | null) => (ms === null ? '' : new Date(ms - new Date(ms).getTimezoneOffset() * 60000).toISOString().slice(0, 10));
const fromInput = (value: string, endOfDay: boolean) => {
  if (!value) return null;
  const [y, m, d] = value.split('-').map(Number);
  return endOfDay ? new Date(y, m - 1, d + 1).getTime() : new Date(y, m - 1, d).getTime();
};

export function makeRange(preset: RangePreset, custom?: DateRange): RangeValue {
  return { preset, range: rangeForPreset(preset, Date.now(), custom) };
}

export function DateRangeFilter({ value, onChange, presets }: { value: RangeValue; onChange: (v: RangeValue) => void; presets?: RangePreset[] }) {
  const options = presets ? PRESETS.filter((p) => presets.includes(p.value)) : PRESETS;
  return (
    <div className="flex flex-wrap items-center gap-2">
      <label className="sr-only" htmlFor="analytics-range">Date range</label>
      <select
        id="analytics-range"
        value={value.preset}
        onChange={(e) => {
          const preset = e.target.value as RangePreset;
          onChange(makeRange(preset, preset === 'custom' ? { from: value.range.from, to: value.range.to ?? Date.now() } : undefined));
        }}
        className="h-9 rounded-md border border-input bg-background px-2.5 text-sm text-foreground"
      >
        {options.map((p) => (
          <option key={p.value} value={p.value}>
            {p.label}
          </option>
        ))}
      </select>
      {value.preset === 'custom' && (
        <div className="flex items-center gap-1.5 text-xs text-muted-foreground">
          <input
            type="date"
            aria-label="From"
            value={toInput(value.range.from)}
            onChange={(e) => onChange(makeRange('custom', { from: fromInput(e.target.value, false), to: value.range.to }))}
            className="h-9 rounded-md border border-input bg-background px-2 text-sm text-foreground"
          />
          <span>to</span>
          <input
            type="date"
            aria-label="To"
            value={toInput(value.range.to === null ? null : value.range.to - 1)}
            onChange={(e) => onChange(makeRange('custom', { from: value.range.from, to: fromInput(e.target.value, true) }))}
            className="h-9 rounded-md border border-input bg-background px-2 text-sm text-foreground"
          />
        </div>
      )}
    </div>
  );
}
